import type { RequestStatus } from '@core/api/state';
import type { Property } from '@core/models/properties';
import type { FieldSeasonData } from '@core/models/seasons';
import { datadogLogs } from '@datadog/browser-logs';
import type { Lens } from '@dhmk/zustand-lens';
import { loadAssetsAndImages, setAssetsDateRange } from '@redux/imageAssets/thunks';
import type { RootState } from '@redux/reducers';
import { store } from '@redux/store';
import type { Field, RegionDTO, RegionFeatureProperties } from '@state/regions/region.models';
import { selectRegionTreeReferenceDate, selectSeason } from '@state/season/season.selectors';
import type { BoundStore } from '@state/store';
import type { MultiPolygon, Polygon } from '@turf/turf';
import dayjs from 'dayjs';
import type { AnyAction } from 'redux';
import type { EntityState, Update } from 'redux-ngrx-entity';
import { createEntityAdapter } from 'redux-ngrx-entity';
import type { ThunkDispatch } from 'redux-thunk';
import { EmptyRegionTreeError, fetchRegions } from './region.actions';
import { getInitialRegionState } from './region.initialize';
import type { Region } from './region.models';

export interface RegionState extends EntityState<Region> {
  status: RequestStatus;
  selectedRegionId: string;
  selectedField?: {
    id: string;
    regionId: string;
  } | null;
  propertyFeatureCollection: GeoJSON.FeatureCollection<
    Polygon | MultiPolygon,
    RegionFeatureProperties
  >;
  propertyId?: string;
  regionTree?: RegionDTO | null;
  emptyHistoricalSnapshotDate?: string;
  favoriteFieldsId: string[];
  // Easy scout
  easyScoutFieldHover: string[];
  // Field hovered on any map (numeric region_source feature id); shared so replica maps (slider /
  // side-by-side) both highlight the same field border.
  hoveredFieldId: number | null;
}

interface RegionRefreshOptions {
  skipCache?: boolean;
}

interface RegionActions {
  fetch: (property: Property, seasons?: FieldSeasonData[]) => void;
  // Re-reads the region tree in place: no 'loading' status, no scene reset, selection kept.
  refresh: (
    property: Property,
    seasons: FieldSeasonData[],
    options?: RegionRefreshOptions
  ) => Promise<void>;
  updateRegion: (changes: Update<Region>) => void;
  updateField: (regionId: string, changes: Update<Field>) => void;
  updateFields: (regionId: string, changes: Array<Update<Field>>) => void;
  setSelectedField: (data: RegionState['selectedField']) => void;
  setSelectedRegion: (id: string) => void;
  toggleFavoriteFieldId: (id: string) => void;
  setEasyScoutFieldHover: (ids: string[]) => void;
  setHoveredFieldId: (id: number | null) => void;
  reset: () => void;
}

export type RegionSlice = RegionState & RegionActions;

const regionAdapter = createEntityAdapter<Region>();
const fieldAdapter = createEntityAdapter<Field>();

const initialState: RegionState = getInitialRegionState();

// Only the latest fetch/refresh may write to the store, so a slow response for a previous farm
// (or an older refresh) never overwrites newer data.
let latestRequestId = 0;

function resolveSelectedField(
  selectedField: RegionState['selectedField'],
  entities: RegionState['entities']
): RegionState['selectedField'] {
  if (!selectedField) return selectedField;

  const region = Object.values(entities).find((item) => item?.fields.entities[selectedField.id]);

  return region ? { id: selectedField.id, regionId: region.id } : null;
}

function resolveSelectedRegionId(selectedRegionId: string, entities: RegionState['entities']) {
  return selectedRegionId === 'root' || entities[selectedRegionId] ? selectedRegionId : 'root';
}

export const createRegionSlice: Lens<RegionSlice, BoundStore> = (set, get, api) => ({
  ...initialState,
  fetch: (property, seasons) => {
    const requestId = ++latestRequestId;
    const isNewProperty = get().propertyId !== property.id;
    const referenceDate = selectRegionTreeReferenceDate(api.getState());
    set((state) => ({
      ...regionAdapter.removeAll(state),
      status: 'loading',
      emptyHistoricalSnapshotDate: undefined,
    }));
    fetchRegions(property, seasons, { referenceDate })
      .then(({ data, collection, tree }) => {
        if (requestId !== latestRequestId) return;
        set((state) => {
          const next = regionAdapter.addAll(data, state);
          return {
            ...next,
            status: 'success',
            propertyFeatureCollection: collection,
            regionTree: tree,
            propertyId: property.id,
            selectedField: resolveSelectedField(state.selectedField, next.entities),
            selectedRegionId: resolveSelectedRegionId(state.selectedRegionId, next.entities),
          };
        });
        if (isNewProperty) {
          const startDate = dayjs().subtract(10, 'year').toDate();
          const endDate = dayjs().toDate();
          (store.dispatch as ThunkDispatch<RootState, undefined, AnyAction>)(
            setAssetsDateRange([startDate, endDate], property.id)
          );
        }
      })
      .catch(() => {
        if (requestId !== latestRequestId) return;
        set((state) => ({ ...regionAdapter.removeAll(state), status: 'error' }));
      });
  },
  refresh: async (property, seasons, { skipCache = false } = {}) => {
    const { status, propertyId } = get();
    if (status !== 'success' || propertyId !== property.id) return;

    const requestId = ++latestRequestId;
    const referenceDate = selectRegionTreeReferenceDate(api.getState());
    const isHistorical = selectSeason(api.getState())?.season.active === false;
    set(() => ({ emptyHistoricalSnapshotDate: undefined }));

    try {
      const { data, collection, tree } = await fetchRegions(property, seasons, {
        skipCache,
        referenceDate,
      });
      if (requestId !== latestRequestId) return;

      const previousRegionId = get().selectedRegionId;
      set((state) => {
        const next = regionAdapter.addAll(data, state);

        return {
          ...next,
          propertyFeatureCollection: collection,
          regionTree: tree,
          selectedField: resolveSelectedField(state.selectedField, next.entities),
          selectedRegionId: resolveSelectedRegionId(state.selectedRegionId, next.entities),
        };
      });
      if (requestId === latestRequestId && get().selectedRegionId !== previousRegionId) {
        // The season change may have loaded scenes for a region absent from this snapshot.
        // Reload for the accepted selection, retaining the existing imagery date range.
        await (store.dispatch as ThunkDispatch<RootState, undefined, AnyAction>)(
          loadAssetsAndImages(property.id)
        );
      }
    } catch (error) {
      if (requestId !== latestRequestId) return;

      if (error instanceof EmptyRegionTreeError) {
        if (isHistorical) {
          // An empty historical date does not mean the property has no fields today.
          set(() => ({ emptyHistoricalSnapshotDate: referenceDate }));
          return;
        }
        // The current snapshot lost its last field: same outcome as reloading an empty property.
        set((state) => ({
          ...regionAdapter.removeAll(state),
          status: 'error',
          selectedField: null,
        }));
        return;
      }

      datadogLogs.logger.error('regions.refresh: failed to reload the region tree', {
        error: error instanceof Error ? error.message : String(error),
        propertyId: property.id,
      });
    }
  },
  updateRegion: (changes: Update<Region>) => {
    set((state) => ({ ...regionAdapter.updateOne(changes, state) }));
  },
  updateField: (regionId: string, changes: Update<Field>) => {
    set((state) => ({
      ...regionAdapter.updateOne(
        {
          id: regionId,
          changes: {
            fields: fieldAdapter.updateOne(changes, state.entities[regionId].fields),
          },
        },
        state
      ),
    }));
  },
  updateFields: (regionId, changes) => {
    set((state) => ({
      ...regionAdapter.updateOne(
        {
          id: regionId,
          changes: {
            fields: fieldAdapter.updateMany(changes, state.entities[regionId].fields),
          },
        },
        state
      ),
    }));
  },
  setSelectedField: (data) => {
    set(() => ({ selectedField: data }));
  },
  setSelectedRegion: (data) => {
    set((state) => {
      const newRegion = state.entities[data];

      return {
        selectedRegionId: newRegion?.isRoot ? 'root' : data,
      };
    });
  },
  toggleFavoriteFieldId: (fieldId) => {
    set((state) => {
      if (state.favoriteFieldsId.includes(fieldId)) {
        return { favoriteFieldsId: state.favoriteFieldsId.filter((id) => id !== fieldId) };
      }

      return { favoriteFieldsId: [...state.favoriteFieldsId, fieldId] };
    });
  },
  setEasyScoutFieldHover: (data) => {
    set(() => ({ easyScoutFieldHover: data }));
  },
  setHoveredFieldId: (id) => {
    set((state) => (state.hoveredFieldId === id ? state : { hoveredFieldId: id }));
  },
  reset: () => {
    // Drops any in-flight fetch/refresh of the previous property.
    latestRequestId++;
    set(() => ({ ...getInitialRegionState(), emptyHistoricalSnapshotDate: undefined }));
  },
});
